import "./PersonalData.scss";

import clsx from "clsx";
import { useAtom } from "jotai";
import { FC, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { ChevronDown } from "@/assets/icons/icons";
import { Button } from "@/components/Interface/Button/Button";
import { Link } from "@/components/Interface/Link/Link";
import { Notification } from "@/components/Interface/Notification/Notification";
import { ProfileCard } from "@/components/Layout/BaseLayouts/ProfileCard/ProfileCard";
import { TwoColumnLayout } from "@/components/Layout/BaseLayouts/TwoColumnLayout";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { FieldRow } from "@/components/Portal/UserAccount/PersonalDetails/FieldRow/FieldRow";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useGetCustomer, useGetIdentificationStatus } from "@/services/api";
import { BasicCustomerData } from "@/services/model";
import { userAccountStateAtom } from "@/utils/atoms";
import { getCountryAlpha3FromId } from "@/utils/countries";
import {
  getLocalizedBirthday,
  getPhoneFromBasicCustomerDataPhone,
  getSalutationTranslationKey,
  isBusinessCustomer,
  transformBasicCustomerDataToPersonalDetailsForm,
} from "@/utils/customerUtils";

import { PersonalDataForm } from "./PersonalDataForm";

const SalutationOption: FC<{ label: string; selected: boolean }> = ({
  label,
  selected,
}) => (
  <div className="flex items-center gap-3">
    <div
      className={clsx(
        "flex size-6 shrink-0 items-center justify-center rounded-full",
        selected ? "bg-surface-secondary" : "border-2 border-border-tertiary",
      )}
    >
      {selected && (
        <div className="size-2 rounded-full bg-foreground-tertiary" />
      )}
    </div>
    <span className="text-base font-semibold leading-md text-foreground-tertiary">
      {label}
    </span>
  </div>
);

export const PersonalData: FC = () => {
  const [userAccountState, setUserAccountState] = useAtom(userAccountStateAtom);
  // The local state of the customer, not the same as the backend response!
  const [localCustomerData, setLocalCustomerData] = useState<
    BasicCustomerData | undefined
  >();
  const isMobile = useIsMobile();
  const { t, i18n } = useTranslation();
  const { t: tForm } = useTranslation("translation", {
    keyPrefix: "Onboarding.sections.personal-details.form",
  });

  const { data: customer, isLoading } = useGetCustomer();
  const { data: identificationStatus } = useGetIdentificationStatus({
    query: {
      enabled: !!customer,
    },
  });

  useEffect(() => {
    if (customer) {
      setLocalCustomerData(customer);
    }
  }, [customer]);

  /** Set the form to the current customerData as prefilled values,
   * so the user can easily edit data.
   * Also, force the dateOfBirth as a localized string,
   * because passed in Date gets serialized weirdly.
   **/
  const formData =
    localCustomerData && userAccountState.editingPersonalData
      ? transformBasicCustomerDataToPersonalDetailsForm(localCustomerData)
      : undefined;

  const shouldShowCustomerForm =
    userAccountState.editingPersonalData &&
    formData &&
    identificationStatus &&
    customer;

  const canClickChangeButton =
    !userAccountState.editingMail && !userAccountState.editingPassword;

  const salutationId = localCustomerData?.salutation?.id;
  const isBusiness =
    !!localCustomerData && isBusinessCustomer(localCustomerData);

  const goodToKnow = (
    <Notification
      title={t("Content.goodToKnow")}
      description={
        <Trans
          components={[
            <Link
              key="0"
              href={t("translation:links.supportHomeLink")}
              target="_blank"
              rel="noreferrer"
              size="sm"
            />,
          ]}
          i18nKey="Onboarding.sections.personal-details.disabledFieldsHint"
        />
      }
    />
  );

  const personalDataCard = shouldShowCustomerForm ? (
    <PersonalDataForm
      localCustomerData={localCustomerData}
      setLocalCustomerData={setLocalCustomerData}
      formData={formData}
      identificationStatus={identificationStatus}
      aside={isMobile ? goodToKnow : undefined}
    />
  ) : (
    <ProfileCard
      title={t("Onboarding.sections.personal-details.title")}
      stickyHeader={false}
      maxWidth="none"
      actions={
        canClickChangeButton && (
          <Button
            dataTestid="change-personal-data-button"
            variant="primary"
            size="sm"
            onClick={() =>
              setUserAccountState((prev) => ({
                ...prev,
                editingPersonalData: true,
              }))
            }
          >
            {t("portal:user-account.personalDetails.change")}
          </Button>
        )
      }
    >
      <div className="flex flex-row flex-wrap gap-x-4 gap-y-3">
        <SalutationOption
          label={t(getSalutationTranslationKey({ id: 2 }))}
          selected={salutationId === 2}
        />
        <SalutationOption
          label={t(getSalutationTranslationKey({ id: 1 }))}
          selected={salutationId === 1}
        />
        <SalutationOption
          label={t(getSalutationTranslationKey({ id: 0 }))}
          selected={
            salutationId !== undefined &&
            salutationId !== 1 &&
            salutationId !== 2
          }
        />
      </div>

      <FieldRow
        label={tForm("placeholder.firstName")}
        value={localCustomerData?.firstName}
        disabled
      />
      <FieldRow
        label={tForm("placeholder.lastName")}
        value={localCustomerData?.lastName}
        disabled
      />
      <FieldRow
        dataTestid="dateOfBirth-display"
        // No required marker here (tForm's label carries a "*")
        label={t("portal:user-account.personalDetails.dateOfBirth")}
        value={getLocalizedBirthday(localCustomerData?.birthday, i18n.language)}
        disabled
      />
      <FieldRow
        label={tForm("placeholder.phone")}
        value={getPhoneFromBasicCustomerDataPhone(localCustomerData?.phone)}
      />

      {isBusiness && (
        <div className="flex flex-col gap-4">
          <h4 className="text-base font-semibold leading-none text-foreground-primary">
            {t("portal:user-account.personalDetails.companyData.title")}
          </h4>
          <FieldRow
            label={t(
              "Onboarding.sections.personal-details.form.label.organizationName",
            )}
            value={localCustomerData?.companyName}
          />
          <FieldRow
            label={t(
              "Onboarding.sections.personal-details.form.label.organizationForm",
            )}
            value={localCustomerData?.legalForm?.description}
          />
          <FieldRow
            label={t(
              "Onboarding.sections.personal-details.form.label.organizationId",
            )}
            value={localCustomerData?.registerNo}
          />
        </div>
      )}

      <div className="flex flex-col gap-4">
        <h4 className="text-base font-semibold leading-none text-foreground-primary">
          {t("portal:user-account.personalDetails.address")}
        </h4>
        <div className="grid grid-cols-1 gap-4 tablet:grid-cols-2">
          <FieldRow
            label={tForm("placeholder.street")}
            value={localCustomerData?.mainAddress?.street}
          />
          <FieldRow
            label={tForm("placeholder.houseNumber")}
            value={localCustomerData?.mainAddress?.houseNumber}
          />
        </div>
        <FieldRow
          label={`${tForm("placeholder.addressSupplement")} ${t("label.optional")}`}
          value={localCustomerData?.mainAddress?.postalDeliveryNotes}
        />
        <div className="grid grid-cols-1 gap-4 tablet:grid-cols-2">
          <FieldRow
            label={tForm("placeholder.zip")}
            value={localCustomerData?.mainAddress?.zip}
          />
          <FieldRow
            label={tForm("placeholder.city")}
            value={localCustomerData?.mainAddress?.city}
          />
        </div>
        <FieldRow
          label={tForm("placeholder.country")}
          value={t(
            `countries.${getCountryAlpha3FromId(
              localCustomerData?.mainAddress?.country?.id,
            )}`,
            { defaultValue: "-" },
          )}
          icon={
            <ChevronDown className="size-6 shrink-0 stroke-current text-foreground-primary" />
          }
        />
      </div>
    </ProfileCard>
  );

  return (
    <div id="user-account-personal-data">
      {isLoading && <LoadingSpinner />}

      <TwoColumnLayout
        rows={[
          {
            left: personalDataCard,
            right: shouldShowCustomerForm && !isMobile ? goodToKnow : undefined,
          },
        ]}
      />
    </div>
  );
};
